<template>
  <div class="v3-widget v3-panel v3-hero" v-testid="testid">
    <V3Skeleton v-if="showResumeSkeleton" shape="hero" :heading="false" />
    <template v-else>
    <span v-if="heroIcon" class="v3-ic" :class="`v3-ic--${heroIconVariant}`">
      <span class="ms">{{ heroIcon }}</span>
    </span>
    <div class="v3-hero-main">
      <div v-if="hr.eyebrow.value" class="v3-hero-eyebrow">{{ hr.eyebrow.value }}</div>
      <h2 class="v3-hero-title" v-testid="testid + '-title'">{{ heroTitle }}</h2>
      <div v-if="heroMeta" class="v3-hero-meta">{{ heroMeta }}</div>
      <div v-if="isResume && hr.totalSteps.value" class="v3-hero-progress" :aria-label="hr.stepText.value">
        <span class="v3-hero-progress-fill" :style="{ width: hr.progressPct.value + '%' }"></span>
      </div>
      <div v-if="isNext" class="v3-hero-meta">{{ hr.nextGuidedLine.value }}</div>
    </div>
    <div v-if="ctaText" class="v3-hero-cta">
      <button type="button" class="v3-hero-btn" v-testid="testid + '-cta'" @click="onCta">{{ ctaText }}</button>
    </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { getCurrentInstance } from "vue";
import { heroResume } from "@rm-ui/homepage/heroResume";
import { canNavigateTo } from "@/composables/useV3Nav";
import V3Skeleton from "./cLandingV3Skeleton";

// New-portal (v3) Hero — prototype V3Hero, flow-agnostic. When `resume` is on
// (default): an in-progress evaluation (POST Report/homeResume) renders step
// N-of-M progress + Continue; otherwise the next READY task from the Vuex tasks
// store renders the guided "Start here" spotlight; with neither, the configured
// static hero. Sibling: beta HeroWidget.vue.
export default {
  name: "cLandingHero",
  components: { V3Skeleton },
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const store = getCurrentInstance().proxy.$store;
    const hr = heroResume({
      http: axios,
      getEnabled: () => props.definition.resume !== false && !props.editMode,
      getTasks: () => store.state.tasks.taskList,
    });
    return { hr };
  },
  created() {
    if (this.definition.resume !== false && !this.editMode) this.$store.dispatch("tasks/init");
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3Hero";
    },
    // Resume-mode skeleton while the in-progress lookup is still loading (so the
    // static hero doesn't flash before the resume item swaps in).
    showResumeSkeleton() {
      return this.definition.resume !== false && !this.editMode && this.hr.loading.value && !this.hr.hasResume.value;
    },
    isResume() {
      return this.definition.resume !== false && this.hr.hasResume.value;
    },
    // Prototype "Start here": nothing in progress → surface the next READY task.
    isNext() {
      return this.definition.resume !== false && !this.isResume && this.hr.hasNext.value;
    },
    heroIcon() {
      return this.isResume ? this.hr.icon.value : this.isNext ? this.hr.nextIcon.value : this.definition.icon || "rocket_launch";
    },
    heroIconVariant() {
      return this.isResume || this.isNext ? "accent" : this.definition.iconVariant || "accent";
    },
    heroTitle() {
      return this.isResume ? this.hr.title.value : this.isNext ? this.hr.nextTitle.value : this.definition.title || "";
    },
    heroMeta() {
      if (this.isNext) return this.hr.nextMetaLine.value;
      if (!this.isResume) return this.definition.subtitle || this.definition.description || "";
      const saved = this.hr.lastSavedText.value ? `Last saved ${this.hr.lastSavedText.value}` : "";
      return [this.hr.typeLabel.value, this.hr.stepText.value, saved].filter(Boolean).join(" · ");
    },
    ctaText() {
      if (this.isResume) return this.hr.continueLabel.value;
      if (this.isNext) return this.hr.startLabel.value;
      // Static-state CTA is permission-gated like the legacy landing config
      // (resume/start-next CTAs open a document the user already has a task on).
      const action = this.definition.ctaAction || "create";
      if (!this.editMode && !canNavigateTo(this, action)) return "";
      return this.definition.ctaLabel || "";
    },
  },
  methods: {
    onCta() {
      if (this.editMode) return;
      if (this.isResume) {
        const reference = this.hr.reference.value;
        if (!reference) return;
        // A classification in progress continues on the levelling/evaluate page
        // (LevellingEvaluation), not the document view.
        if (String(this.hr.flowType.value || "").toUpperCase() === "CLASSIFICATION") {
          this.$router.push({ name: "evaluate", params: { docReference: reference } }).catch(() => {});
          return;
        }
        this.$emit("openDocument", { system_number: reference, reference });
        return;
      }
      if (this.isNext) {
        const reference = this.hr.nextReference.value;
        if (reference) this.$emit("openDocument", { system_number: reference, reference });
        return;
      }
      const action = this.definition.ctaAction || "create";
      if (action === "create") {
        this.$emit("addDocument", "");
        return;
      }
      this.$router.push({ name: action }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
